import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Loader2, Image as ImageIcon, PlayCircle, RefreshCw, X } from "lucide-react";
import { api, errorMessage, publicAssetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const FILTERS = [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "expired", label: "Expired" },
    { key: "", label: "All" },
];

const statusPill = (s) => {
    const map = {
        pending: "bg-amber-100 text-amber-700",
        approved: "bg-emerald-100 text-emerald-700",
        rejected: "bg-rose-100 text-rose-700",
        paused: "bg-slate-100 text-slate-600",
        expired: "bg-slate-100 text-slate-500",
    };
    return map[s] || "bg-slate-100 text-slate-600";
};

/**
 * Admin moderation queue for merchant-submitted ads. Approve or reject
 * with an inline reason dialog. Uses the same tokens as the other admin
 * pages (Poppins headings, `.card-soft` cards, orange primary).
 */
export default function AdminMerchantAdsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("pending");
    const [rejectFor, setRejectFor] = useState(null); // ad object to reject
    const [reason, setReason] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const q = status ? `?status=${status}` : "";
            const res = await api.get(`/admin/merchant-ads${q}`);
            setItems(res.data.items || []);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    }, [status]);

    useEffect(() => { load(); }, [load]);

    const approve = async (ad) => {
        try {
            await api.post(`/admin/merchant-ads/${ad.id}/approve`);
            toast.success(`Approved: ${ad.headline}`);
            load();
        } catch (e) { toast.error(errorMessage(e)); }
    };

    const submitReject = async () => {
        if (!rejectFor || !reason.trim()) return;
        try {
            await api.post(`/admin/merchant-ads/${rejectFor.id}/reject`, { reason: reason.trim() });
            toast.success(`Rejected: ${rejectFor.headline}`);
            setRejectFor(null); setReason(""); load();
        } catch (e) { toast.error(errorMessage(e)); }
    };

    const counts = useMemo(() => {
        const c = { pending: 0, approved: 0, rejected: 0 };
        for (const a of items) if (c[a.status] !== undefined) c[a.status]++;
        return c;
    }, [items]);

    return (
        <div className="space-y-6" data-testid="admin-merchant-ads-page">
            <header>
                <div className="font-heading text-sm font-semibold text-[var(--brand-orange)]">Ads</div>
                <h1 className="mt-1 font-heading text-3xl font-extrabold text-[var(--text-primary)] sm:text-4xl">
                    Merchant Ad Moderation
                </h1>
                <p className="mt-1 max-w-2xl font-body text-sm text-[var(--text-muted)]">
                    Approve or reject merchant-submitted ads. Approved ads appear on the customer surfaces automatically.
                </p>
                <div className="mt-3 flex flex-wrap gap-2 font-body text-xs">
                    <span className="status-pill status-pill-neutral">{items.length} in view</span>
                    <span className="status-pill" style={{ background: "#FEF3C7", color: "#92400E" }}>{counts.pending} pending</span>
                    <span className="status-pill" style={{ background: "#DCFCE7", color: "#166534" }}>{counts.approved} approved</span>
                </div>
            </header>

            <div className="card-soft flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                    {FILTERS.map((f) => (
                        <Button
                            key={f.key || "all"}
                            variant={status === f.key ? "default" : "outline"}
                            onClick={() => setStatus(f.key)}
                            className={status === f.key ? "btn-primary rounded-full" : "rounded-full border-slate-200"}
                            data-testid={`admin-ads-filter-${f.key || "all"}`}
                        >
                            {f.label}
                        </Button>
                    ))}
                </div>
                <Button
                    variant="outline"
                    onClick={load}
                    className="rounded-xl border-slate-200"
                    disabled={loading}
                    data-testid="admin-ads-refresh"
                >
                    <RefreshCw size={14} className={"mr-2 " + (loading ? "animate-spin" : "")} /> Refresh
                </Button>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {Array.from({ length: 4 }).map((_, i) => <div key={i} className="card-soft h-48 animate-pulse !bg-slate-100" />)}
                </div>
            ) : items.length === 0 ? (
                <div className="card-soft flex flex-col items-center gap-3 py-16 text-center">
                    <Megaphone size={32} className="text-[var(--brand-orange)]" />
                    <h2 className="font-heading text-lg font-bold text-[var(--text-primary)]">
                        Nothing in this queue
                    </h2>
                    <p className="max-w-md font-body text-sm text-[var(--text-muted)]">
                        Approved ads will appear here once merchants submit them.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {items.map((a) => (
                        <AdModerationCard
                            key={a.id}
                            ad={a}
                            onApprove={() => approve(a)}
                            onReject={() => { setRejectFor(a); setReason(""); }}
                        />
                    ))}
                </div>
            )}

            <Dialog open={!!rejectFor} onOpenChange={(v) => (!v && setRejectFor(null))}>
                <DialogContent data-testid="admin-ad-reject-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Reject advertisement</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="font-body text-sm text-[var(--text-muted)]">
                            Explain why this ad can't be published. The merchant will see this reason.
                        </p>
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Artwork is blurry, headline promises unlicensed discount, etc."
                            className="min-h-[100px]"
                            data-testid="admin-ad-reject-reason"
                        />
                    </div>
                    <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
                        <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
                        <Button
                            onClick={submitReject}
                            disabled={!reason.trim()}
                            className="btn-primary"
                            data-testid="admin-ad-reject-confirm"
                        >
                            Confirm rejection
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function AdModerationCard({ ad, onApprove, onReject }) {
    const isVideo = ad.ad_type === "video";
    const preview = ad.artwork_url || ad.video_url;
    return (
        <div className="card-soft flex flex-col gap-3" data-testid={`admin-ad-mod-card-${ad.id}`}>
            <div className="flex gap-3">
                <div className="h-28 w-40 shrink-0 overflow-hidden rounded-xl bg-[#FFF3E6]">
                    {preview ? (
                        isVideo ? (
                            <div className="flex h-full w-full items-center justify-center bg-black text-white">
                                <PlayCircle size={28} />
                            </div>
                        ) : (
                            <img src={publicAssetUrl(preview)} alt={ad.headline} className="h-full w-full object-cover" />
                        )
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-[var(--brand-orange)]">
                            <ImageIcon size={26} />
                        </div>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <div className="line-clamp-1 font-heading text-base font-bold text-[var(--text-primary)]">
                                {ad.headline}
                            </div>
                            <div className="font-body text-xs text-[var(--text-muted)]">
                                by <b>{ad.merchant_name}</b> · {ad.ad_type.replace("_", " ")}
                            </div>
                        </div>
                        <span className={"rounded-full px-2 py-0.5 font-heading text-[10px] font-bold uppercase " + statusPill(ad.status)}>
                            {ad.status}
                        </span>
                    </div>
                    {ad.subline && <div className="mt-1 line-clamp-2 font-body text-xs text-[var(--text-muted)]">{ad.subline}</div>}
                    <div className="mt-2 font-body text-[11px] text-[var(--text-muted)]">
                        {(ad.district_slugs?.length || 0) === 0 ? "All Kerala" : ad.district_slugs.join(", ")} · {ad.start_date} → {ad.expiry_date}
                    </div>
                </div>
            </div>
            {ad.rejection_reason && (
                <div className="rounded-xl bg-rose-50 px-3 py-2 font-body text-xs text-rose-700">
                    <b>Previous rejection:</b> {ad.rejection_reason}
                </div>
            )}
            {ad.status === "pending" && (
                <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-2">
                    <Button
                        variant="outline"
                        onClick={onReject}
                        className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"
                        data-testid="admin-ad-reject-btn"
                    >
                        <X size={14} className="mr-1" /> Reject
                    </Button>
                    <Button
                        onClick={onApprove}
                        className="btn-primary rounded-full"
                        data-testid="admin-ad-approve-btn"
                    >
                        Approve
                    </Button>
                </div>
            )}
        </div>
    );
}
